import { useEffect } from "react";
import { useCallbackRef } from "ui";

export const isMac =
  typeof navigator !== "undefined"
    ? /Mac|iPhone|iPod|iPad/.test(navigator.platform)
    : false;

/**
 * Runs `callback` when the user presses the platform command modifier
 * (⌘ on mac, Ctrl elsewhere) together with `key`, e.g. ⌘K / Ctrl+K.
 *
 * The default browser behaviour for the combo is prevented, so this can be
 * used to take over shortcuts like Ctrl+F (find) for in-app search.
 */
export function useCommandKey(key: string, callback: () => void) {
  const callbackRef = useCallbackRef(callback);

  useEffect(() => {
    const target = key.toLowerCase();

    const handler = (e: KeyboardEvent) => {
      if (!e.key || e.key.toLowerCase() !== target) return;

      // mac uses meta, others use ctrl
      if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
        e.preventDefault();
        callbackRef();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [key, callbackRef]);
}
